import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader2, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { syncUserInfo } from '../services/api';
import '../styles/VerifyEmail.css';

const CALLBACK_URL = `${window.location.origin}/auth/callback`;

function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isEmailVerified } = useAuth();

  const initialEmail = useMemo(() => location.state?.email || user?.email || '', [location.state, user]);
  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const run = async () => {
      if (user && isEmailVerified) {
        try {
          await syncUserInfo();
        } catch {
          // ignore
        }
        navigate('/dashboard', { replace: true });
      }
    };

    run();
  }, [isEmailVerified, navigate, user]);

  const handleResend = async () => {
    setError('');
    setMessage('');

    if (!supabase) {
      setError('Supabaseが設定されていません。');
      return;
    }
    if (!email) {
      setError('メールアドレスを入力してください。');
      return;
    }

    setSubmitting(true);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: CALLBACK_URL },
      });
      if (resendError) throw resendError;
      setMessage('認証メールを再送しました。受信トレイをご確認ください。');
    } catch {
      setError('認証メールの再送に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="verify-email-page">
      <div className="verify-background">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
      </div>

      <div className="verify-container">
        <header className="verify-header">
          <div className="verify-logo-section">
            <div className="verify-university-name">弘前大学</div>
            <div className="verify-app-name">Clinical Clerkship</div>
          </div>
        </header>

        <main className="verify-card">
          <div className="verify-card-content">
            <div className="verify-icon-wrapper">
              <Mail className="verify-header-icon" size={32} />
            </div>

            <h1 className="verify-title">メール認証</h1>
            <p className="verify-subtitle">
              登録したメールアドレス宛に確認メールを送信しました。<br />
              メール内のリンクをクリックして認証を完了してください。
            </p>

            {error && (
              <div className="verify-auth-alert verify-auth-alert--error" role="alert">
                <AlertCircle className="icon" size={18} />
                <div>{error}</div>
              </div>
            )}

            {message && (
              <div className="verify-auth-alert verify-auth-alert--success" role="status">
                <CheckCircle className="icon" size={18} />
                <div>{message}</div>
              </div>
            )}

            <div className="verify-form">
              <div className="verify-form-group">
                <label htmlFor="email">
                  メールアドレス（再送先）
                </label>
                <div className="verify-input-wrapper">
                  <Mail className="verify-input-icon" size={18} />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="verify-input"
                    placeholder="university@hirosaki-u.ac.jp"
                    autoComplete="email"
                  />
                </div>
              </div>

              <button type="button" onClick={handleResend} disabled={submitting} className="verify-submit-button">
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} style={{ marginRight: 8 }} />
                    送信中...
                  </>
                ) : (
                  '認証メールを再送'
                )}
              </button>

              <div className="verify-links-row">
                <Link to="/login" className="verify-action-link">
                  ログイン画面へ
                </Link>
                <Link to="/forgot-password" className="verify-action-link">
                  パスワード再設定
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default VerifyEmail;

